import React, { useEffect, useState } from 'react';
import {
  etatSamedi, fetchHoraires, jourCourt, jourLong, joursAVenir, plagesTexte, samedisAVenir, statutMaintenant, texteJour,
  dateLocale, plagesTelephone, statutTelephone,
} from '../lib/horaires';

// Horaires RÉELS lus dans le portail (chantier #20889). Rien au pré-rendu react-snap (le HTML figerait « ce samedi :
// ouvert » au jour du build) ; sans réponse du portail, seul le texte habituel de la page reste : jamais un
// « ouvert » inventé.
export function useHorairesAtelier() {
  const [etat, setEtat] = useState({ statut: 'fixe', data: null });
  useEffect(() => {
    if (typeof navigator !== 'undefined' && navigator.userAgent === 'ReactSnap') return undefined;
    let actif = true;
    fetchHoraires()
      .then((data) => actif && setEtat({ statut: 'ok', data }))
      .catch(() => actif && setEtat({ statut: 'erreur', data: null }));
    return () => { actif = false; };
  }, []);
  return etat;
}

const CE_SAMEDI = { fr: 'Ce samedi', en: 'This Saturday', es: 'Este sábado', de: 'Diesen Samstag', it: 'Questo sabato', pt: 'Este sábado' };
const PROCHAIN = { fr: 'prochain samedi ouvert', en: 'next open Saturday', es: 'próximo sábado abierto', de: 'nächster geöffneter Samstag', it: 'prossimo sabato aperto', pt: 'próximo sábado aberto' };
const OUVERT = { fr: 'ouvert', en: 'open', es: 'abierto', de: 'geöffnet', it: 'aperto', pt: 'aberto' };
const FERME = { fr: 'fermé', en: 'closed', es: 'cerrado', de: 'geschlossen', it: 'chiuso', pt: 'fechado' };

export function LigneSamedi({ lang, texteFixe }) {
  const { statut, data } = useHorairesAtelier();
  const e = statut === 'ok' ? etatSamedi(data) : null;
  if (!e) return <span data-testid="ligne-samedi">{texteFixe}</span>;
  const quel = `${CE_SAMEDI[lang] || CE_SAMEDI.fr} (${jourCourt(e.samedi.date, lang)})`;
  const dp = lang === 'fr' ? ' : ' : ': '; // espace avant les deux-points : en français seulement
  let texte;
  if (e.ouvert) texte = `${quel}${dp}${OUVERT[lang] || OUVERT.fr} ${plagesTexte(e.samedi.sans_rdv, lang)}`;
  else if (e.prochainOuvert) texte = `${quel}${dp}${FERME[lang] || FERME.fr} · ${PROCHAIN[lang] || PROCHAIN.fr}${dp}${jourCourt(e.prochainOuvert.date, lang)}`;
  else texte = `${quel}${dp}${FERME[lang] || FERME.fr}`;
  return <span data-testid="ligne-samedi">{texte}</span>;
}

// Badge d'état : `prefixe` dit DE QUOI on parle (« Atelier », « Téléphone ») — l'accueil à l'atelier et l'accueil
// téléphonique sont deux choses différentes (William, 23/10/2026).
function Badge({ s, prefixe, testid, lang }) {
  return (
    <span
      data-testid={testid}
      className={`inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-semibold border ${s.ouvert ? 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30' : 'bg-white/5 text-slate-300 border-white/10'}`}
    >
      <span className={`h-2 w-2 rounded-full ${s.ouvert ? 'bg-emerald-400' : 'bg-slate-500'}`} aria-hidden="true" />
      {prefixe ? `${prefixe}${lang === 'fr' ? ' : ' : ': '}` : ''}{s.texte}
    </span>
  );
}

export function BadgeTelephone({ lang, prefixe }) {
  const { statut, data } = useHorairesAtelier();
  if (statut !== 'ok') return null;
  return <Badge s={statutTelephone(data, lang)} prefixe={prefixe} testid="badge-telephone" lang={lang} />;
}

export function PlagesTelephone({ lang }) {
  const { statut, data } = useHorairesAtelier();
  return <span data-testid="plages-telephone">{plagesTelephone(statut === 'ok' ? data : null, lang)}</span>;
}

export function BadgeOuverture({ lang, prefixe }) {
  const { statut, data } = useHorairesAtelier();
  if (statut !== 'ok') return null;
  const s = statutMaintenant(data, lang);
  if (prefixe) return <Badge s={s} prefixe={prefixe} testid="badge-ouverture" lang={lang} />;
  return (
    <span
      data-testid="badge-ouverture"
      className={`inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-semibold border ${s.ouvert ? 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30' : 'bg-white/5 text-slate-300 border-white/10'}`}
    >
      <span className={`h-2 w-2 rounded-full ${s.ouvert ? 'bg-emerald-400' : 'bg-slate-500'}`} aria-hidden="true" />
      {s.texte}
    </span>
  );
}

const INDISPO = {
  fr: 'Les horaires détaillés sont momentanément indisponibles. En cas de doute, appelez-nous au 01 83 62 33 27.',
  en: 'Detailed hours are temporarily unavailable. If in doubt, call us on +33 1 83 62 33 27.',
  es: 'El horario detallado no está disponible por el momento. En caso de duda, llámenos al +33 1 83 62 33 27.',
  de: 'Die genauen Zeiten sind vorübergehend nicht verfügbar. Rufen Sie uns im Zweifel an: +33 1 83 62 33 27.',
  it: 'Gli orari dettagliati non sono momentaneamente disponibili. In caso di dubbio, chiamateci al +33 1 83 62 33 27.',
  pt: 'O horário detalhado está temporariamente indisponível. Em caso de dúvida, ligue-nos para +33 1 83 62 33 27.',
};

function Ligne({ jour, lang }) {
  return (
    <div className="flex items-center justify-between py-2.5 border-b border-white/5 last:border-0">
      <span className="text-slate-300">{jourLong(jour.date, lang)}</span>
      <span className={jour.ferme || !(jour.sans_rdv || []).length ? 'text-slate-500' : 'text-[#0099D6] font-semibold'}>{texteJour(jour, lang)}</span>
    </div>
  );
}

export function JoursAVenir({ lang, nombre = 14 }) {
  const { statut, data } = useHorairesAtelier();
  if (statut === 'erreur') return <p className="text-slate-400 text-sm" data-testid="horaires-indisponibles">{INDISPO[lang] || INDISPO.fr}</p>;
  if (statut !== 'ok') return null;
  const jours = joursAVenir(data).filter((j) => dateLocale(j.date).getDay() !== 0).slice(0, nombre);
  return <div data-testid="tableau-jours">{jours.map((j) => <Ligne key={j.date} jour={j} lang={lang} />)}</div>;
}

export function SamedisAVenir({ lang, nombre = 6 }) {
  const { statut, data } = useHorairesAtelier();
  if (statut !== 'ok') return null;
  const samedis = samedisAVenir(data, new Date(), nombre);
  if (!samedis.length) return null;
  return <div data-testid="liste-samedis">{samedis.map((j) => <Ligne key={j.date} jour={j} lang={lang} />)}</div>;
}
